import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ShopCtx = createContext(null);
export const useShop = () => useContext(ShopCtx);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
};

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => load("mf_cart", []));
  const [wishlist, setWishlist] = useState(() => load("mf_wish", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => { localStorage.setItem("mf_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("mf_wish", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => null); }, []);
  const reloadCategories = useCallback(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => null);
  }, []);

  // Sync wishlist with backend when user signs in
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        // Push any local items to server (merge)
        const local = load("mf_wish", []);
        await Promise.all(local.map((w) => api.post("/account/wishlist", { product_id: w.product_id }).catch(() => null)));
        // Then pull authoritative list
        const r = await api.get("/account/wishlist");
        if (cancelled) return;
        const items = r.data.map((p) => ({
          product_id: p.id, slug: p.slug, name: p.name, image: p.images?.[0] || "", price: p.price,
        }));
        setWishlist(items);
      } catch (err) {
        // Sync is best-effort; localStorage still holds the wishlist for next attempt.
        // eslint-disable-next-line no-console
        console.warn("Wishlist sync failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const exist = prev.find((i) => i.product_id === product.id);
      if (exist) {
        return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.id, slug: product.slug, name: product.name,
        image: product.images?.[0] || "", price: product.price, quantity: qty,
      }];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const removeFromCart = useCallback((product_id) => {
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const updateQty = useCallback((product_id, quantity) => {
    if (quantity <= 0) {
      setCart((prev) => prev.filter((i) => i.product_id !== product_id));
      return;
    }
    setCart((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    const item = {
      product_id: product.id, slug: product.slug, name: product.name,
      image: product.images?.[0] || "", price: product.price,
    };
    setWishlist((prev) => {
      const exists = prev.find((i) => i.product_id === product.id);
      if (exists) {
        toast(`${product.name} removed from wishlist`);
        if (user) api.delete(`/account/wishlist/${product.id}`).catch(() => null);
        return prev.filter((i) => i.product_id !== product.id);
      }
      toast.success(`${product.name} added to wishlist`);
      if (user) api.post("/account/wishlist", { product_id: product.id }).catch(() => null);
      return [...prev, item];
    });
  }, [user]);

  const isWishlisted = useCallback((id) => wishlist.some((i) => i.product_id === id), [wishlist]);

  const openCartDrawer = useCallback(() => setCartOpen(true), []);
  const closeCartDrawer = useCallback(() => setCartOpen(false), []);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = useMemo(
    () => ({ cart, wishlist, addToCart, removeFromCart, updateQty, clearCart, toggleWishlist, isWishlisted, cartCount, cartSubtotal, cartOpen, openCartDrawer, closeCartDrawer, categories, reloadCategories }),
    [cart, wishlist, addToCart, removeFromCart, updateQty, clearCart, toggleWishlist, isWishlisted, cartCount, cartSubtotal, cartOpen, openCartDrawer, closeCartDrawer, categories, reloadCategories]
  );

  return <ShopCtx.Provider value={value}>{children}</ShopCtx.Provider>;
}
